import React, { useState } from 'react'
import { 
  Building2, 
  MapPin, 
  DollarSign, 
  Search,
  Heart,
  Eye,
  Download
} from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Input } from '../components/ui/Input'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'

interface Property {
  id: string
  name: string
  type: 'DST' | 'Direct'
  sponsor: string
  location: string
  price: number
  minInvestment: number
  expectedReturn: number
  image: string
  description: string
  features: string[]
  isQualified: boolean
  isFavorite: boolean
}

export const MarketplacePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<'all' | 'DST' | 'Direct'>('all')
  const [selectedSponsor, setSelectedSponsor] = useState<string>('all')
  const [isLoading, setIsLoading] = useState(false)

  const properties: Property[] = [
    {
      id: '1',
      name: 'Ares Industrial Portfolio',
      type: 'DST',
      sponsor: 'ARES',
      location: 'Dallas, TX',
      price: 50000000,
      minInvestment: 50000,
      expectedReturn: 6.5,
      image: '/api/placeholder/400/300',
      description: 'Diversified industrial portfolio across major US markets with strong tenant credit profiles.',
      features: ['Industrial', 'Multi-tenant', 'Grade A', 'Long-term leases'],
      isQualified: true,
      isFavorite: false
    },
    {
      id: '2',
      name: 'JLL Office Tower',
      type: 'Direct',
      sponsor: 'JLL',
      location: 'Austin, TX',
      price: 25000000,
      minInvestment: 100000,
      expectedReturn: 7.2,
      image: '/api/placeholder/400/300',
      description: 'Class A office building in downtown Austin with premium amenities and strong occupancy.',
      features: ['Office', 'Class A', 'Downtown', 'Premium amenities'],
      isQualified: true,
      isFavorite: true
    },
    {
      id: '3',
      name: 'Inland Retail Center',
      type: 'DST',
      sponsor: 'Inland',
      location: 'Phoenix, AZ',
      price: 35000000,
      minInvestment: 25000,
      expectedReturn: 5.8,
      image: '/api/placeholder/400/300',
      description: 'Well-located retail center with national tenants and strong foot traffic.',
      features: ['Retail', 'National tenants', 'High traffic', 'Stable income'],
      isQualified: true,
      isFavorite: false
    },
    {
      id: '4',
      name: 'Newmark Multifamily',
      type: 'Direct',
      sponsor: 'Newmark',
      location: 'Denver, CO',
      price: 40000000,
      minInvestment: 75000,
      expectedReturn: 6.8,
      image: '/api/placeholder/400/300',
      description: 'Modern multifamily complex with luxury amenities and strong rental demand.',
      features: ['Multifamily', 'Luxury', 'Modern amenities', 'High demand'],
      isQualified: true,
      isFavorite: false
    }
  ]

  const sponsors = ['ARES', 'JLL', 'Inland', 'Newmark']

  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = selectedType === 'all' || property.type === selectedType
    const matchesSponsor = selectedSponsor === 'all' || property.sponsor === selectedSponsor
    
    return matchesSearch && matchesType && matchesSponsor
  })

  const toggleFavorite = (propertyId: string) => {
    // In a real app, this would update the backend
    console.log('Toggle favorite:', propertyId)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
            <Building2 className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            Property Marketplace
          </h1>
          <p className="text-secondary-600 dark:text-slate-400 mt-1">
            Browse qualified replacement properties from institutional sponsors
          </p>
        </div>
        <Button onClick={() => setIsLoading(true)} disabled={isLoading}>
          {isLoading ? (
            <LoadingSpinner size="sm" variant="icon" className="mr-2" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export Properties
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400 dark:text-slate-500" />
              <Input
                placeholder="Search properties or locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as 'all' | 'DST' | 'Direct')}
              className="input w-full"
            >
              <option value="all">All Types</option>
              <option value="DST">DST</option>
              <option value="Direct">Direct</option>
            </select>
          </div>
          
          <div>
            <select
              value={selectedSponsor}
              onChange={(e) => setSelectedSponsor(e.target.value)}
              className="input w-full"
            >
              <option value="all">All Sponsors</option>
              {sponsors.map(sponsor => (
                <option key={sponsor} value={sponsor}>{sponsor}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Properties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProperties.map((property) => (
          <Card key={property.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative">
              <div className="h-48 bg-secondary-200 dark:bg-slate-700 flex items-center justify-center">
                <Building2 className="h-16 w-16 text-secondary-400 dark:text-slate-500" />
              </div>
              <div className="absolute top-3 right-3 flex space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 backdrop-blur-sm"
                  onClick={() => toggleFavorite(property.id)}
                >
                  <Heart className={`h-4 w-4 ${property.isFavorite ? 'fill-red-500 text-red-500' : 'text-secondary-400 dark:text-slate-400'}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-800 backdrop-blur-sm"
                >
                  <Eye className="h-4 w-4 text-secondary-400 dark:text-slate-400" />
                </Button>
              </div>
              <div className="absolute top-3 left-3">
                <Badge variant={property.isQualified ? 'success' : 'warning'}>
                  {property.isQualified ? 'Qualified' : 'Pending'}
                </Badge>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-slate-100">{property.name}</h3>
                <Badge variant="secondary">{property.type}</Badge>
              </div>
              
              <div className="flex items-center text-secondary-600 dark:text-slate-400 mb-3">
                <MapPin className="h-4 w-4 mr-1" />
                <span className="text-sm">{property.location}</span>
              </div>
              
              <p className="text-sm text-secondary-600 dark:text-slate-400 mb-4 line-clamp-2">
                {property.description}
              </p>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-600 dark:text-slate-400">Total Price</span>
                  <span className="font-medium text-secondary-900 dark:text-slate-100">${(property.price / 1000000).toFixed(1)}M</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-600 dark:text-slate-400">Min Investment</span>
                  <span className="font-medium text-secondary-900 dark:text-slate-100">${property.minInvestment.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-600 dark:text-slate-400">Expected Return</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{property.expectedReturn}%</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-1 mb-4">
                {property.features.slice(0, 2).map((feature, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {feature}
                  </Badge>
                ))}
                {property.features.length > 2 && (
                  <Badge variant="secondary" className="text-xs">
                    +{property.features.length - 2} more
                  </Badge>
                )}
              </div>
              
              <div className="flex space-x-2">
                <Button className="flex-1">
                  View Details
                </Button>
                <Button variant="outline" size="sm">
                  <Heart className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredProperties.length === 0 && (
        <Card className="p-12 text-center">
          <Building2 className="h-16 w-16 text-secondary-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-slate-100 mb-2">
            No properties found
          </h3>
          <p className="text-secondary-600 dark:text-slate-400">
            Try adjusting your search criteria or filters to find more properties.
          </p>
        </Card>
      )}

      {/* Featured Sponsors */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-4">Featured Sponsors</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sponsors.map((sponsor) => (
            <div key={sponsor} className="text-center p-4 border border-secondary-200 dark:border-slate-700 rounded-lg hover:border-primary-300 dark:hover:border-primary-500 transition-colors">
              <div className="h-12 w-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Building2 className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-medium text-secondary-900 dark:text-slate-100">{sponsor}</h3>
              <p className="text-sm text-secondary-600 dark:text-slate-400">Institutional Sponsor</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
